import { LIVE_CLOSE_HOUSEHOLD_CHANGED, type Id, type LiveClientMessage, type LiveServerMessage } from '@odhaczka/core';
import { useSyncExternalStore } from 'react';
import { getMeta } from '../db/db';
import { currentUserId, onAuthChange, refreshSession } from '../lib/auth';
import { syncEngine } from './engine';

// Socket /api/live: serwer daje znać („poke”), że w domu coś się zmieniło — wtedy od razu synchronizujemy.
// To tylko przyspieszenie: dane i tak płyną przez push/pull, a 30-sekundowe odpytywanie zostaje jako zapas.

const MIN_RETRY_MS = 1_000;
// Po awarii serwera wracamy w ciągu ~10 s (dłuższy odstęp opóźniał zmiany na żywo).
const MAX_RETRY_MS = 10_000;
/** Serwer wysyła hb co 25 s; po minucie ciszy połączenie uznajemy za martwe. */
const DEAD_AFTER_MS = 60_000;
/** Po powrocie do aplikacji łączymy od nowa, jeśli przez tyle nic nie przyszło. */
const STALE_ON_RETURN_MS = 30_000;
const WATCHDOG_MS = 10_000;

let ws: WebSocket | null = null;
let connected = false;
let started = false;
let attempt = 0;
let lastMessageMs = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let viewing: Id | null = null;
const listeners = new Set<() => void>();

function setConnected(next: boolean) {
  if (connected === next) return;
  connected = next;
  for (const fn of listeners) fn();
}

function send(msg: LiveClientMessage) {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
}

// Aplikacja w tle nie „ogląda” listy — wtedy serwer ma wysłać powiadomienie push.
const sendView = () => send({ type: 'view', listId: document.visibilityState === 'visible' ? viewing : null });

/** Porzuca bieżący socket bez czekania na jego zamknięcie (martwe TCP potrafi zamykać się minutami). */
function drop() {
  const sock = ws;
  ws = null;
  setConnected(false);
  if (!sock) return;
  sock.onopen = sock.onmessage = sock.onclose = sock.onerror = null;
  try {
    sock.close();
  } catch {
    // ignorujemy
  }
}

function connect() {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  drop();
  if (!navigator.onLine || !currentUserId()) return;
  const sock = new WebSocket(`${location.origin.replace(/^http/, 'ws')}/api/live`);
  ws = sock;
  lastMessageMs = Date.now();
  sock.onopen = () => {
    attempt = 0;
    lastMessageMs = Date.now();
    setConnected(true);
    send({ type: 'hello', clientId: syncEngine.clientId });
    sendView();
    // Zmiany z czasu bez połączenia.
    syncEngine.schedule(0);
  };
  sock.onmessage = (ev) => {
    lastMessageMs = Date.now();
    let msg: LiveServerMessage;
    try {
      msg = JSON.parse(String(ev.data)) as LiveServerMessage;
    } catch {
      return;
    }
    if (msg.type === 'poke' && msg.clientId !== syncEngine.clientId) void onPoke(msg.version);
    else if (msg.type === 'household') {
      // Ktoś dołączył lub odszedł: nowi członkowie (imiona) i ich dane.
      void import('../data/household').then((m) => m.refreshHousehold());
      syncEngine.schedule(0);
    }
  };
  sock.onclose = (ev) => {
    if (ws !== sock) return;
    ws = null;
    setConnected(false);
    if (ev.code === LIVE_CLOSE_HOUSEHOLD_CHANGED) {
      // Dom scalony z innym: sesja wskaże nowy dom, a silnik zacznie pull od zera.
      attempt = 0;
      void refreshSession().finally(() => {
        if (!ws) scheduleRetry();
      });
      return;
    }
    scheduleRetry();
  };
}

async function onPoke(version: number) {
  if (version > ((await getMeta<number>('cursor')) ?? 0)) syncEngine.schedule(0);
}

function scheduleRetry() {
  if (retryTimer) clearTimeout(retryTimer);
  if (!navigator.onLine || !currentUserId()) return;
  // 1, 2, 4… 30 s plus rozrzut, żeby telefony po awarii serwera nie wracały jednocześnie.
  const delay = Math.min(MAX_RETRY_MS, MIN_RETRY_MS * 2 ** attempt) + Math.random() * MIN_RETRY_MS;
  attempt += 1;
  retryTimer = setTimeout(connect, delay);
}

function reconnectNow() {
  attempt = 0;
  connect();
}

/** Nowe połączenie, chyba że obecne niedawno coś odebrało. */
function reconnectIfQuiet() {
  if (ws && Date.now() - lastMessageMs <= STALE_ON_RETURN_MS) return;
  reconnectNow();
}

/** Uruchamia połączenie na żywo (raz, po sprawdzeniu sesji). */
export function startLive() {
  if (started) return;
  started = true;
  window.addEventListener('online', reconnectIfQuiet);
  window.addEventListener('offline', drop);
  // Zamrożona karta nadal odpowiada na ping, więc serwer musi wprost usłyszeć, że listy nikt nie ogląda.
  document.addEventListener('visibilitychange', () => {
    sendView();
    if (document.visibilityState === 'visible') reconnectIfQuiet();
  });
  window.addEventListener('pagehide', () => send({ type: 'view', listId: null }));
  window.addEventListener('pageshow', () => {
    sendView();
    reconnectIfQuiet();
  });
  setInterval(() => {
    if (ws && Date.now() - lastMessageMs > DEAD_AFTER_MS) {
      drop();
      scheduleRetry();
    }
  }, WATCHDOG_MS);
  onAuthChange(() => {
    if (!currentUserId()) {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null;
      drop();
    } else if (!ws && !retryTimer) reconnectNow();
  });
  connect();
}

/** Lista, którą użytkownik ma teraz otwartą (null = żadna); serwer nie wysyła mu wtedy powiadomień o niej. */
export function setViewingList(listId: Id | null) {
  if (viewing === listId) return;
  viewing = listId;
  sendView();
}

export function useLiveConnected(): boolean {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => void listeners.delete(fn);
    },
    () => connected,
  );
}
